/**
 * Reuses retail catalogue image search (SerpAPI + remove-bg) but persists to Project Catalog.
 */
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { notifyProductImagePreparation } from "@/lib/productImagePreparationFeedback";
import { useProjectCatalogMemoryStatus } from "@/hooks/useProjectCatalogMemoryStatus";
import { cn } from "@/lib/utils";
import type { DtoolsStagingLine } from "@shared/dtoolsJsonStaging";
import { PROJECT_CATALOG_SEED_FAILURE_TOAST } from "@shared/projectCatalogMemoryFlag";
import { ImagePlus, Loader2, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

const CAN_EDIT = new Set(["sales", "admin"]);

type Props = {
  line: DtoolsStagingLine;
  onSaved: (imageUrl: string, projectCatalogueId: number, catalogSaved: boolean) => void;
  compact?: boolean;
  catalogMemoryActive?: boolean;
};

export function normalizeSearchImages(
  images: Array<{ original?: string | null; thumbnail?: string | null; title?: string | null }> | undefined,
) {
  return (images ?? []).filter(
    (img): img is { original: string; thumbnail: string; title?: string | null } =>
      Boolean(img?.original?.trim()) && Boolean(img?.thumbnail?.trim()),
  );
}

export default function ProjectCatalogFindImageButton({
  line,
  onSaved,
  compact,
  catalogMemoryActive: catalogMemoryActiveProp,
}: Props) {
  const { user } = useAuth();
  const catalogMemory = useProjectCatalogMemoryStatus();
  const catalogMemoryActive = catalogMemoryActiveProp ?? catalogMemory.memoryActive;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const searchSeed = [line.manufacturer, line.model].filter(Boolean).join(" ").trim();
  const [imageSearchText, setImageSearchText] = useState(searchSeed);
  const [manualUrl, setManualUrl] = useState(line.imageUrl ?? "");
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [finalUrl, setFinalUrl] = useState<string | null>(null);
  const [showManualFallback, setShowManualFallback] = useState(false);
  const [showQueryEditor, setShowQueryEditor] = useState(false);
  const [searchToastShown, setSearchToastShown] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const trimmedSearchText = imageSearchText.trim();
  const canEdit =
    !!user &&
    CAN_EDIT.has(String(user.role)) &&
    Boolean(line.manufacturer?.trim()) &&
    Boolean(line.model?.trim()) &&
    Boolean(line.catalogueKey?.trim());

  const searchImages = trpc.catalogue.searchProductImages.useQuery(
    { query: trimmedSearchText, limit: 20 },
    {
      enabled: open && step === 1 && trimmedSearchText.length >= 2,
      retry: false,
      refetchOnMount: "always",
    },
  );
  const removeBg = trpc.catalogue.removeImageBackground.useMutation();
  const saveCatalog = trpc.salesSandbox.saveProjectCatalogLine.useMutation({
    onError: (e) => toast.error(e.message || "Failed to save image."),
  });
  const uploadPhoto = trpc.projectCatalogueSandbox.uploadPhoto.useMutation({
    onError: (e) => toast.error(e.message || "Failed to upload image."),
  });

  const persistFinalImage = async (imageUrl: string) => {
    let catalogSaved = false;
    let projectCatalogueId = 0;

    if (catalogMemoryActive) {
      try {
        const res = await saveCatalog.mutateAsync({
          catalogueKey: line.catalogueKey,
          manufacturer: line.manufacturer,
          model: line.model,
          category: line.category,
          subcategory: line.subcategory,
          displayName: [line.manufacturer, line.model].filter(Boolean).join(" "),
          catalogId: line.catalogId,
          componentId: line.componentId,
          dtoolsItemId: line.dtoolsItemId,
          originalDtoolsDescription: line.dtoolsDescription,
          polishedDescription: line.polishedDescription,
          imageUrl,
          unitPrice: line.unitPrice,
          bucket: line.bucket,
          isAccessory: line.isAccessory,
          systemName: line.systemName,
          roomName: line.roomName,
          overwriteImage: true,
          incrementUsage: false,
        });
        if (res.ok === false || ("skipped" in res && res.skipped)) {
          toast.warning(PROJECT_CATALOG_SEED_FAILURE_TOAST, {
            description: "warning" in res ? String(res.warning) : undefined,
          });
        } else {
          catalogSaved = true;
          projectCatalogueId = Number(res.id ?? 0);
          toast.success("Saved to Project Catalog");
        }
      } catch {
        // Session apply still proceeds below.
      }
    } else {
      toast.message("Image applied to this proposal only", {
        description: catalogMemory.message,
      });
    }

    onSaved(imageUrl, projectCatalogueId, catalogSaved);
    setOpen(false);
  };

  const handleLocalUpload = async (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    setUploadPending(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const raw = String(reader.result ?? "");
          resolve(raw.includes(",") ? raw.split(",")[1] ?? "" : raw);
        };
        reader.onerror = () => reject(reader.error ?? new Error("Read failed"));
        reader.readAsDataURL(file);
      });
      const uploaded = await uploadPhoto.mutateAsync({
        filename: file.name,
        contentType: file.type,
        base64,
      });
      notifyProductImagePreparation(uploaded);
      await persistFinalImage(uploaded.imageUrl);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not upload image.");
    } finally {
      setUploadPending(false);
    }
  };

  const resultImages = useMemo(
    () => normalizeSearchImages(searchImages.data?.images),
    [searchImages.data?.images],
  );

  useEffect(() => {
    if (!open || step !== 1 || searchImages.isFetching || searchImages.isLoading) return;
    if (searchImages.isError) {
      if (!searchToastShown) {
        toast.error("Unable to find images for this product.");
        setSearchToastShown(true);
      }
      return;
    }
    if (searchImages.isSuccess && resultImages.length === 0 && !searchToastShown) {
      toast.message("No images found.");
      setSearchToastShown(true);
    }
  }, [
    open,
    step,
    searchImages.isFetching,
    searchImages.isLoading,
    searchImages.isError,
    searchImages.isSuccess,
    resultImages.length,
    searchToastShown,
  ]);

  if (!canEdit) return null;

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className={compact ? "h-8 text-[11px]" : "h-8 text-xs"}
        onClick={() => {
          setStep(1);
          setImageSearchText(searchSeed);
          setSelectedUrl(null);
          setFinalUrl(null);
          setManualUrl(line.imageUrl ?? "");
          setShowManualFallback(false);
          setShowQueryEditor(false);
          setSearchToastShown(false);
          setOpen(true);
        }}
      >
        <ImagePlus className="mr-1.5 h-3.5 w-3.5" />
        Find image
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {step === 1 ? "Choose product image" : step === 2 ? "Preparing image…" : "Confirm product image"}
            </DialogTitle>
            <DialogDescription>
              {line.manufacturer} {line.model} — search results, or upload an image from your device.
            </DialogDescription>
          </DialogHeader>
          <input
            ref={uploadInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              e.target.value = "";
              void handleLocalUpload(file);
            }}
          />
          <div className="min-h-[340px] space-y-3">
            {step === 1 ? (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  {showQueryEditor ? (
                    <div className="flex min-w-0 flex-1 gap-2">
                      <Input value={imageSearchText} onChange={(e) => setImageSearchText(e.target.value)} />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setSelectedUrl(null);
                          setSearchToastShown(false);
                          void searchImages.refetch();
                        }}
                      >
                        Search
                      </Button>
                    </div>
                  ) : (
                    <Button type="button" variant="ghost" size="sm" onClick={() => setShowQueryEditor(true)}>
                      Edit search query
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={uploadPending || uploadPhoto.isPending}
                    onClick={() => uploadInputRef.current?.click()}
                    data-testid="project-catalog-upload-image"
                  >
                    {uploadPending || uploadPhoto.isPending ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Upload className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Upload Image
                  </Button>
                </div>
                {searchImages.isFetching ? (
                  <div className="flex h-44 items-center justify-center text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Finding product images…
                  </div>
                ) : resultImages.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
                    {resultImages.slice(0, 20).map((img) => (
                      <button
                        key={img.original}
                        type="button"
                        onClick={() => setSelectedUrl(img.original)}
                        className={cn(
                          "overflow-hidden rounded border bg-muted/20",
                          selectedUrl === img.original ? "border-primary ring-2 ring-primary/50" : "border-border",
                        )}
                      >
                        <img
                          src={img.thumbnail}
                          alt={img.title ?? "Search result thumbnail"}
                          className="h-24 w-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2 rounded border bg-muted/20 p-3 text-sm text-muted-foreground">
                    <p>
                      {searchImages.isError
                        ? "Unable to find images for this product."
                        : searchImages.data?.configured === false
                          ? "Image search is not configured. Paste an image URL manually."
                          : searchImages.data?.message || "No images found."}
                    </p>
                    {!showManualFallback ? (
                      <Button
                        type="button"
                        variant="link"
                        className="px-0"
                        onClick={() => setShowManualFallback(true)}
                      >
                        Paste image URL manually
                      </Button>
                    ) : null}
                  </div>
                )}
                {showManualFallback ? (
                  <div className="flex gap-2">
                    <Input value={manualUrl} onChange={(e) => setManualUrl(e.target.value)} placeholder="https://..." />
                    <Button type="button" variant="outline" onClick={() => setSelectedUrl(manualUrl.trim())}>
                      Use URL
                    </Button>
                  </div>
                ) : null}
              </>
            ) : null}
            {step === 2 ? (
              <div className="flex h-56 flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                Preparing image…
              </div>
            ) : null}
            {step === 3 && finalUrl ? (
              <img src={finalUrl} alt="Final image preview" className="mx-auto max-h-72 rounded object-contain" />
            ) : null}
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            {step === 1 ? (
              <>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setSelectedUrl(null);
                      setSearchToastShown(false);
                      void searchImages.refetch();
                    }}
                  >
                    Try Again
                  </Button>
                  <Button
                    type="button"
                    disabled={!selectedUrl}
                    onClick={async () => {
                      if (!selectedUrl) return;
                      setStep(2);
                      try {
                        const r = await removeBg.mutateAsync({ imageUrl: selectedUrl });
                        setFinalUrl(r.imageUrl || selectedUrl);
                        notifyProductImagePreparation(r);
                      } catch {
                        setFinalUrl(selectedUrl);
                        notifyProductImagePreparation({ usedFallback: true });
                      } finally {
                        setStep(3);
                      }
                    }}
                  >
                    Next
                  </Button>
                </div>
              </>
            ) : null}
            {step === 3 ? (
              <>
                <Button type="button" variant="outline" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button
                  type="button"
                  disabled={saveCatalog.isPending || !finalUrl}
                  onClick={async () => {
                    if (!finalUrl) return;
                    await persistFinalImage(finalUrl);
                  }}
                >
                  Save image
                </Button>
              </>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
